// ============================================================
// NAV CONFIG — catálogo de módulos do SisCodel
// Fonte única de verdade para o menu (NavDrawer) e para o
// controle de acesso (lib/permissoes.js) — evita duplicação/desalinhamento.
// ============================================================
import {
  Package, Cpu, Truck, FolderOpen, Warehouse, Users, BarChart2, ShieldCheck, ClipboardList, Landmark, Gauge, RefreshCw,
} from 'lucide-react';

// Ordem dos grupos e dos acessos internos: alfabética, com o Dashboard de
// cada grupo fixo no topo. Únicas exceções ao ordenamento alfabético dos
// grupos: 'Controle de Acesso', 'Logs de Auditoria' e 'Sincronizações', que
// ficam fixos no fim (ver NavDrawer.jsx).
export const NAV = [
  {
    id: 'documentos_menu',
    label: 'Documentos',
    icon: FolderOpen,
    color: '#0891b2',
    colorDim: '#ecfeff',
    colorBorder: '#67e8f9',
    children: [
      // Mantém o id 'documentos' de quando era item solto: é o que está
      // gravado em perfil_modulos_permitidos e no card do Menu Principal.
      { id: 'documentos', label: 'Licenças e Cadastros' },
      // Espelho local do MTR-MG, consulta de manifesto e certificado. Saiu
      // de Relatórios porque é tela de trabalho (sincroniza, lista pendência),
      // não relatório. As futuras telas de escrita ('mtr_operacao', já
      // reconhecido por api/feam.js) entram neste mesmo grupo.
      { id: 'mtr_painel', label: 'Painel de Manifestos (MTR)' },
    ],
  },
  {
    id: 'entradas_menu',
    label: 'Entradas',
    icon: Package,
    color: 'var(--green)',
    colorDim: 'var(--green-dim)',
    colorBorder: '#86efac',
    children: [
      { id: 'dashboard_entradas', label: 'Dashboard de Entradas' },
      { id: 'atendimentos',       label: 'Controle de Atendimentos' },
      { id: 'entrada',            label: 'Controle de Entradas' },
    ],
  },
  {
    id: 'estoque_menu',
    label: 'Estoque',
    icon: Warehouse,
    color: '#d97706',
    colorDim: '#fef3c7',
    colorBorder: '#f59e0b',
    children: [
      { id: 'dashboard_estoque',   label: 'Dashboard' },
      { id: 'acondicionamento',    label: 'Estoque por Prédios' },
      { id: 'estoque_recipientes', label: 'Estoque por Recipientes' },
    ],
  },
  {
    id: 'financeiro_menu',
    label: 'Financeiro',
    icon: Landmark,
    color: '#166534',
    colorDim: '#f0fdf4',
    colorBorder: '#4ade80',
    children: [
      { id: 'relatorio_financeiro', label: 'Dashboard Financeiro' },
      { id: 'conta_colaboradores',  label: 'Conta Colaboradores' },
      // Saldo de contas correntes (extrato OFX) + aplicações (digitado) — dado
      // sensível, mesmo tratamento admin-only dos outros fin_*
      { id: 'fin_contas_bancos',    label: 'Contas e Bancos', adminOnly: true },
      { id: 'caixa',                label: 'Controle de Caixa' },
      { id: 'convenios',            label: 'Convênios' },
      // Custo por kg do desmonte (rateio pelo valor de venda) e estoque a
      // custo — lê folha e financeiro, mesmo tratamento admin-only dos fin_*
      { id: 'fin_custo_operacao',   label: 'Custo da Operação', adminOnly: true },
      // Financeiro gerencial: dado sensível (folha), só admin — o RLS das
      // tabelas fin_* também exige is_admin(), a trava não é só de tela.
      { id: 'fin_dre',              label: 'DRE · Resultado do mês', adminOnly: true },
      { id: 'faturas_saida',        label: 'Faturas' },
      // Títulos a pagar/receber em aberto no sistema financeiro — mesmo
      // tratamento admin-only dos outros fin_*
      { id: 'fin_fluxo_caixa',      label: 'Fluxo de Caixa Projetado', adminOnly: true },
      { id: 'guia_plano_contas',    label: 'Guia do Plano de Contas' },
      // NFS-e do Portal Nacional (emitidas e recebidas). O mesmo id é o
      // módulo exigido por api/nfse.js para sincronizar.
      { id: 'notas_servico',        label: 'NF de Serviço' },
      { id: 'notas_fiscais',        label: 'NF de Entrada Própria' },
      { id: 'fin_plano_contas',     label: 'Plano de Contas', adminOnly: true },
      { id: 'fin_receitas_despesas', label: 'Receitas e Despesas', adminOnly: true },
    ],
  },
  {
    id: 'indicadores_menu',
    label: 'Indicadores',
    icon: Gauge,
    color: 'var(--green)',
    colorDim: 'var(--green-dim)',
    colorBorder: '#86efac',
    children: [
      { id: 'indicadores',          label: 'Indicadores Geral' },
      // Mesmos indicadores, com os blocos em R$ — só admin (RLS das tabelas
      // ind_historico_financeiro/ind_metas/fin_* também exige is_admin()).
      { id: 'indicadores_lideres',  label: 'Indicadores Líderes', adminOnly: true },
    ],
  },
  {
    id: 'parceiros',
    label: 'Parceiros',
    icon: Users,
    color: '#0f766e',
    colorDim: '#f0fdfa',
    colorBorder: '#5eead4',
    children: [
      { id: 'parceiros_crm',       label: 'CRM · Relacionamento' },
      { id: 'parceiros_lista',     label: 'Listagem de Parceiros' },
    ],
  },
  {
    id: 'processamentos_menu',
    label: 'Processamentos',
    icon: Cpu,
    color: 'var(--blue)',
    colorDim: 'var(--blue-dim)',
    colorBorder: '#93c5fd',
    children: [
      { id: 'dashboard_processamentos', label: 'Dashboard de Processamentos' },
      { id: 'composicao_produto', label: 'Composição de Produto' },
      { id: 'processamento',    label: 'Controle de Processamentos' },
      { id: 'desmontagem_colaboradores', label: 'Desmontagem por Colaborador' },
      { id: 'estudo_material',  label: 'Estudo de Material' },
      { id: 'ordens_servico',   label: 'Ordens de Serviço' },
    ],
  },
  {
    id: 'relatorios_menu',
    label: 'Relatórios',
    icon: BarChart2,
    color: '#be185d',
    colorDim: '#fdf2f8',
    colorBorder: '#fbcfe8',
    children: [
      { id: 'relatorio_comissoes', label: 'Comissões por Responsável' },
      // NF-e, cupons e NFS-e emitidos no mês, para a contadora conferir o pacote de
      // XML. Admin-only: nfe_documentos exige is_admin() no RLS.
      { id: 'fin_conferencia_notas', label: 'Contabilidade · Conferência de Notas', adminOnly: true },
      // Títulos pagos e recebidos no mês, para a contabilidade. Admin-only porque
      // mostra salários e retiradas de sócios (dado sensível, mesmo tratamento dos
      // outros fin_*; o RLS de fin_lancamentos/fin_titulos_recebidos também exige
      // is_admin()). Primeiro adminOnly deste grupo: NavDrawer e permissoes.js
      // tratam adminOnly em qualquer grupo, não só no Financeiro.
      { id: 'fin_relatorio_contabil', label: 'Contabilidade · Pagos e Recebidos', adminOnly: true },
      { id: 'relatorio_entradas_estoque', label: 'Entradas no Estoque' },
      { id: 'historico_precos',   label: 'Histórico de Preços' },
      { id: 'relatorio_nf_pev',    label: 'NF de entrada PEV' },
    ],
  },
  {
    id: 'saida_menu',
    label: 'Saídas',
    icon: Truck,
    color: 'var(--purple)',
    colorDim: 'var(--purple-dim)',
    colorBorder: '#c4b5fd',
    children: [
      { id: 'dashboard_saidas', label: 'Dashboard de Saídas' },
      { id: 'saida',            label: 'Controle de Saídas' },
    ],
  },
  {
    id: 'controle_acesso',
    label: 'Controle de Acesso',
    icon: ShieldCheck,
    color: '#b91c1c',
    colorDim: '#fef2f2',
    colorBorder: '#fca5a5',
    adminOnly: true,
  },
  {
    id: 'logs_auditoria',
    label: 'Logs de Auditoria',
    icon: ClipboardList,
    color: '#b91c1c',
    colorDim: '#fef2f2',
    colorBorder: '#fca5a5',
    adminOnly: true,
  },
  {
    id: 'sincronizacoes',
    label: 'Sincronizações',
    icon: RefreshCw,
    color: '#b91c1c',
    colorDim: '#fef2f2',
    colorBorder: '#fca5a5',
    adminOnly: true,
  },
];

export function getParentId(modulo) {
  for (const group of NAV) {
    if (group.id === modulo) return group.id;
    if (group.children?.some(c => c.id === modulo)) return group.id;
  }
  return null;
}
